import {
	GQLCartFragment,
	GQLCartItemFragment,
	GQLMerchDecorationTypeType,
	GQLMerchItemFragment,
	GQLMerchTileFragment,
	GQLOperationalSettingsFragment,
	GQLStoreFragment,
} from '@theprintbar/graphql';
import { LightningBolt } from '@theprintbar/ui';
import {
	calculateDueDate,
	convertToBrisbaneTime,
	getDayLastLetters,
	getDaysUntil,
	getNextBusinessDay,
	isBusinessDay,
	isBusinessDayPostEndOfHours,
	isBusinessDayPreCutoff,
	isFridayPostCutoffOrSaturday,
} from '../../utils';
import { Merch } from './schema';
import { getMerchDecorationMethods } from './utils/getDecorationTypes';

interface MerchTurnaround {
	settings: GQLOperationalSettingsFragment;
	merch?: Merch | GQLMerchTileFragment | GQLMerchItemFragment | null;
	store?: GQLStoreFragment | null;
	useStockedTurnaround?: boolean;
}

export function getMerchTurnaround({ settings, merch, store, useStockedTurnaround }: MerchTurnaround) {
	if (!merch) {
		return 0;
	}

	if (!store && ((merch as GQLMerchItemFragment).preferSameday || (merch as Merch).sameday)) {
		const { turnaround } = getSamedayTurnaroundDetails({ settings, merch });
		return turnaround;
	}

	const decorations = Object.keys(getMerchDecorationMethods(merch));
	let minTimes = [];
	let maxTimes = [];

	if (decorations.includes(GQLMerchDecorationTypeType.Dtg)) {
		minTimes.push(store ? settings.dtgPartnerMinTurnaround : settings.dtgMinTurnaround);
		maxTimes.push(store ? settings.dtgPartnerMaxTurnaround : settings.dtgMaxTurnaround);
	}
	if (decorations.includes(GQLMerchDecorationTypeType.Dtf)) {
		minTimes.push(store ? settings.dtfPartnerMinTurnaround : settings.dtfMinTurnaround);
		maxTimes.push(store ? settings.dtfPartnerMaxTurnaround : settings.dtfMaxTurnaround);
	}
	if (decorations.includes(GQLMerchDecorationTypeType.Emb)) {
		minTimes.push(store ? settings.embPartnerMinTurnaround : settings.embMinTurnaround);
		maxTimes.push(store ? settings.embPartnerMaxTurnaround : settings.embMaxTurnaround);
	}
	if (decorations.includes(GQLMerchDecorationTypeType.Scr)) {
		minTimes.push(store ? settings.scrPartnerMinTurnaround : settings.scrMinTurnaround);
		maxTimes.push(store ? settings.scrPartnerMaxTurnaround : settings.scrMaxTurnaround);
	}

	//Filter out any invalid integer values
	minTimes = minTimes.filter((time: number) => Number.isInteger(time) && time > 0);
	maxTimes = maxTimes.filter((time: number) => Number.isInteger(time) && time > 0);

	let baseTurnaround = Math.max(...(useStockedTurnaround ? minTimes : maxTimes));

	if (isBusinessDayPostEndOfHours()) {
		baseTurnaround += 1;
	}
	return baseTurnaround;
}

export function getSamedayName() {
	let samedayName = isBusinessDayPreCutoff() ? 'Same Day' : 'Next Day';
	if (!isBusinessDay()) {
		if (getDaysUntil(getNextBusinessDay()) === 1) {
			samedayName = 'Next Day';
		} else {
			samedayName = 'Two Day';
		}
	} else if (isFridayPostCutoffOrSaturday()) {
		samedayName = 'Three Day';
	}

	return samedayName;
}

export function getSamedayTurnaroundDetails({ settings, merch }: MerchTurnaround) {
	const details = {
		samedayName: getSamedayName(),
		samedayTitle: settings.samedayPreCutoffTitle,
		samedayDescription: settings.samedayPreCutoffDescription,
		samedayButtonOnTitle: settings.samedayPreCutoffTitleButtonOn,
		samedayButtonOnDescription: settings.samedayPreCutoffDescriptionButtonOn,
		turnaround: 1,
	};

	if (!!merch) {
		//Weekend
		if (!isBusinessDay() || isFridayPostCutoffOrSaturday()) {
			details.samedayTitle = settings.samedayWeekendCutoffTitle;
			details.samedayDescription = settings.samedayWeekendCutoffDescription;
			details.samedayButtonOnTitle = settings.samedayWeekendCutoffTitle;
			details.samedayButtonOnDescription = settings.samedayWeekendCutoffDescription;
		} else if (isBusinessDayPreCutoff()) {
			return details;
		} else {
			details.samedayTitle = settings.samedayPostCutoffTitle;
			details.samedayDescription = settings.samedayPostCutoffDescription;
			details.samedayButtonOnTitle = settings.samedayPostCutoffTitleButtonOn;
			details.samedayButtonOnDescription = settings.samedayPostCutoffDescriptionButtonOn;
		}
	}

	return details;
}

export function getCartTurnaround(settings: GQLOperationalSettingsFragment, cart: GQLCartFragment) {
	if (!cart.items || cart.items.length === 0) {
		return 0;
	}
	const turnarounds = cart.items.map((item: GQLCartItemFragment) => getMerchTurnaround({ settings, merch: item.merch }));
	return Math.max(...turnarounds);
}

export function getTurnaroundString(turnaround: number, includeShipping = true) {
	switch (turnaround) {
		case 1:
			return `${isBusinessDayPreCutoff() ? 'Today' : isFridayPostCutoffOrSaturday() ? 'Monday' : 'Tomorrow'} 4:30pm${
				includeShipping ? ' (excl. shipping)' : ''
			}`;
		default:
			return `${turnaround} business day${turnaround > 1 ? 's' : ''}${includeShipping ? ' (excl. shipping)' : ''}`;
		// const dueDate = calculateDueDate(convertToBrisbaneTime(new Date()), turnaround);
		// const daysOfWeek = ['Sun', 'Mon', 'Tues', 'Wed', 'Thu', 'Fri', 'Sat'];
		// const dayOfWeek = daysOfWeek[dueDate.getDay()];
		// const day = dueDate.getDate();

		// return `${dayOfWeek} ${day}${getDayLastLetters(day)} ${includeShipping ? '(excl shipping)' : ''}`;
	}
}
